import { useEffect, useState } from 'react';
import { listComposes, deleteCompose, type ComposeRecord } from '../../api/compose';
import { composeCoverThumbs } from '../../lib/composeCover';
import { Loader2, Trash2 } from 'lucide-react';

export function ComposeGallery() {
  const [records, setRecords] = useState<ComposeRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listComposes({ limit: 100 });
      setRecords(res.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('确定删除这个搭配？')) return;
    await deleteCompose(id);
    setRecords(records.filter((r) => r.id !== id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--muted)]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载作品...
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-[var(--muted)]">
        <div className="text-4xl mb-3 opacity-20">👗</div>
        还没有保存的搭配作品
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {records.map((r) => (
          <div key={r.id} className="surface overflow-hidden flex flex-col">
            <div className="media-well aspect-square relative bg-[var(--paper-2)]">
              <ComposeCover record={r} />
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="font-medium text-[var(--ink)] truncate">{r.title || '未命名搭配'}</div>
              <div className="text-xs text-[var(--muted)] line-clamp-2 mt-1 flex-1">
                {r.description || '暂无描述'}
              </div>
              {r.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.tags.map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 bg-[var(--accent-soft)] text-[var(--text)] rounded">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] text-[var(--muted)]">
                  {new Date(r.updated_at).toLocaleDateString()}
                </span>
                <button
                  onClick={() => handleDelete(r.id)}
                  className="btn-ghost text-[var(--red)] hover:bg-[var(--red-soft)] p-1"
                  title="删除"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComposeCover({ record }: { record: ComposeRecord }) {
  const thumbs = record.thumbnail_url.trim()
    ? [record.thumbnail_url]
    : composeCoverThumbs(record.items);
  if (thumbs.length === 0) {
    return <div className="text-sm text-[var(--muted)]">{record.items.length} 件单品</div>;
  }
  const grid =
    thumbs.length <= 1 ? 'grid-cols-1' : thumbs.length === 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2';
  return (
    <div className={`absolute inset-0 grid gap-px bg-[var(--line)] ${grid}`}>
      {thumbs.map((src, i) => (
        <div
          key={`${src}-${i}`}
          className={`min-h-0 min-w-0 flex items-center justify-center overflow-hidden bg-[var(--paper-2)] ${
            thumbs.length === 3 && i === 0 ? 'col-span-2' : ''
          }`}
        >
          <img src={src} alt="" />
        </div>
      ))}
    </div>
  );
}
